<template>
  <ClientOnly>
    <Transition name="slide-up">
      <div
        v-if="canShow && !visible && formattedPrice"
        class="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-stone-200 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]"
      >
        <div class="flex items-center justify-between gap-3 px-4 py-3">
          <div class="flex flex-col min-w-0">
            <div class="flex items-center gap-1">
              <span class="text-sm text-stone-500">{{ priceFrom }}</span>
              <div
                v-if="showSaleBadge"
                class="flex items-center bg-orange-50 gap-1 px-2 py-0.5 rounded"
              >
                <LightningChargeFill class="w-3 h-3 text-orange-600 shrink-0" />
                <span class="text-xs font-medium text-orange-600">SALE</span>
              </div>
              <div
                v-else-if="sessionsLabel"
                class="text-xs text-stone-500 bg-teal-50 px-2 py-0.5 rounded"
              >
                {{ sessionsLabel }}
              </div>
            </div>
            <div class="truncate">
              <span class="font-semibold text-body-xl text-stone-900">{{
                formattedPrice
              }}</span>
              <span class="font-normal text-sm text-stone-500">
                / {{ unitWord }}</span
              >
            </div>
          </div>
          <Button class="shrink-0 py-2.5 px-5" @click="handleCtaClick">
            {{ ctaText }}
          </Button>
        </div>
      </div>
    </Transition>
  </ClientOnly>
</template>

<script lang="ts" setup>
import type { EventDescription } from '~/composables/types/event'
import { useEventBuyBoxPrice } from '~/composables/eventBuyBoxPrice'
import { formatSmartPriceAmount } from '~/utils/currencyHelpers'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import {
  sendStickyBuyBoxClick,
  type StickyBuyBoxComponentType,
} from '~/composables/useGtmEvents'
import { useIsSubdomain } from '~/composables/useSubdomain'
import LightningChargeFill from '~/assets/icons/lightning-charge-fill.svg?component'

defineProps<{ visible: boolean }>()
const emit = defineEmits<{ 'scroll-to-buybox': [] }>()

const eventData = inject<EventDescription | undefined>('eventData')
const isSpecialGiftcardEvent = inject<boolean>('isSpecialGiftcardEvent', false)
const eventBuyBoxVisibleElements = {
  giftcard: true,
  ...(inject('eventBuyBoxVisibleElements') as { giftcard?: boolean }),
}
const isSubdomain = useIsSubdomain()
const { t, locale } = useI18n()

const eventRef = computed(() => eventData)

// Use same composable as buybox with empty pricings (no date selected yet).
// This gives correct tiered "from" price (lowest scale + promotion discount from eventData)
// and consistent sale detection — without needing the pricings API.
const { subtitleProps, hasSale } = useEventBuyBoxPrice(
  ref<Record<string, any>>({}),
  {
    dateId: ref(undefined),
    quantity: ref(1),
    event: eventRef,
    isGiftcard: ref(isSpecialGiftcardEvent),
    type: ref('PUBLIC'),
    loadingPricings: ref(false),
  }
)

const priceFrom = computed(() => t('product.show.checkout.priceFrom'))

const formattedPrice = computed(() => {
  if (!eventData) return ''

  // Tiered events: use the composable's computed "from" price (lowest scale + promotion)
  if (subtitleProps.value?.variant === 'tiered') {
    const from = subtitleProps.value.fromPricePerPerson
    const prefix = priceFrom.value + ' '
    return from.startsWith(prefix) ? from.slice(prefix.length) : from
  }

  // Standard / kit: default price amount
  const amount = eventGetters.getDefaultPriceAmount(eventData)
  const currency = eventGetters.getDefaultPriceCurrency(eventData)
  if (!amount) return ''
  return formatSmartPriceAmount(
    Number(amount),
    convertLocaleIntoBCP47(locale.value),
    currency
  )
})

const hasNoDates = computed(
  () => eventGetters.getTotalAvailableDates(eventData) === 0
)

// Mirrors EventDetailsSidebar's shouldEnableGiftcard: a non-team event can offer
// a giftcard, but on a subdomain the supplier must have giftcard embedding on.
const canOfferGiftcard = computed(
  () =>
    !!eventData &&
    !!eventBuyBoxVisibleElements.giftcard &&
    !eventGetters.getIsTeamEvent(eventData) &&
    (!isSubdomain.value ||
      supplierGetters.getHasGiftcardEmbedded(
        eventGetters.getSupplier(eventData)
      ))
)

// Giftcard mode: either an explicit giftcard event or a dateless event that
// falls back to the giftcard CTA. Kept in sync with ctaText below.
const isGiftcardMode = computed(
  () => isSpecialGiftcardEvent || (hasNoDates.value && canOfferGiftcard.value)
)

const unitWord = computed(() => {
  if (!eventData) return ''
  if (isGiftcardMode.value) return t('product.show.checkout.pricePerGiftcard')
  if (eventGetters.isBoxEvent(eventData))
    return t('product.show.card.productBox')
  return t('product.show.checkout.tieredPricing.pricePerTicket')
    .replace('/', '')
    .trim()
})

// Mirror hasSaleOrPromotion from the composable internals:
// hasSale is accurate when pricings API data is present;
// has_promotions covers the no-date-selected state.
const showSaleBadge = computed(
  () => hasSale.value || !!eventData?.has_promotions
)

const ctaText = computed(() =>
  isGiftcardMode.value
    ? t('product.show.checkout.bookGiftcard')
    : t('product.show.checkout.selectDate')
)

const canShow = ref(false)
onMounted(() =>
  setTimeout(() => {
    canShow.value = true
  }, 300)
)

const nextInstance = computed(() => eventGetters.getNextInstance(eventData))

const sessionsCount = computed(() =>
  eventDateGetters.getSessionsCount(nextInstance.value)
)

const sessionsLabel = computed(() =>
  sessionsCount.value
    ? t('product.show.checkout.nSessions', { count: sessionsCount.value })
    : ''
)

// Which variant of the bar was clicked. Precedence mirrors what the bar shows:
// giftcard mode > sale/promotion badge > multidate > tiered > kit > standard.
const componentType = computed<StickyBuyBoxComponentType>(() => {
  if (isGiftcardMode.value) return 'giftcard'
  if (showSaleBadge.value) return 'promotion'
  if (sessionsCount.value > 0) return 'multidate'
  if (subtitleProps.value?.variant === 'tiered') return 'tiered'
  if (eventData && eventGetters.isBoxEvent(eventData)) return 'kit'
  return 'standard'
})

const handleCtaClick = () => {
  sendStickyBuyBoxClick(componentType.value)
  emit('scroll-to-buybox')
}
</script>

<style lang="postcss" scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.25s ease;
  will-change: transform;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

/*
 * The CTA is a fixed element revealed on scroll. On touch devices iOS Safari
 * consumes the first tap as a :hover before firing the click, so the button
 * appears to need two taps (unlike the always-present buy box buttons). Opt
 * into immediate taps and drop the hover-only appearance change on touch, so
 * the first tap registers as a click.
 */
:deep(button) {
  touch-action: manipulation;
}

@media (hover: none) {
  :deep(button:hover) {
    box-shadow: none !important;
  }
}
</style>
